import { PartnerDetailsType, PartnerImage } from "@/types/partner/Partner";

export type ChecklistSectionId =
  | "bankAccount"
  | "pixKey"
  | "businessHours"
  | "additionalInfo";

export interface ChecklistSection {
  id: ChecklistSectionId;
  title: string;
  description: string;
  missing: string[];
}

const SKIP_KEY_PREFIX = "onboardingSkipPartner_";

const hasFilledValue = (value?: string | null): boolean =>
  Boolean(value && value.toString().trim().length > 0);

const hasImageType = (
  partner?: PartnerDetailsType,
  type?: PartnerImage["type"]
): boolean => {
  if (!partner || !type) {
    return false;
  }

  if (
    partner.images?.some(
      (image) =>
        image.type === type &&
        image.status !== "deleted" &&
        hasFilledValue(image.image)
    )
  ) {
    return true;
  }

  if (type === "LOGO" && hasFilledValue(partner.logoImage)) {
    return true;
  }

  return false;
};

export const buildOnboardingChecklist = (
  partner?: PartnerDetailsType | null
): ChecklistSection[] => {
  const sections: ChecklistSection[] = [
    {
      id: "bankAccount",
      title: "Dados bancários",
      description:
        "Conecte sua conta PagBank para receber os valores das vendas.",
      missing: [],
    },
    {
      id: "pixKey",
      title: "Chave Pix",
      description: "Cadastre uma chave Pix para receber transferências.",
      missing: [],
    },
    {
      id: "businessHours",
      title: "Horário de funcionamento",
      description: "Informe os horários em que seu estabelecimento funciona.",
      missing: [],
    },
    {
      id: "additionalInfo",
      title: "Informações adicionais",
      description:
        "Complete os dados que aparecem para os clientes no aplicativo.",
      missing: [],
    },
  ];

  const partnerData = partner || ({} as PartnerDetailsType);

  if (
    !partnerData.pagbankConnect &&
    !hasFilledValue(partnerData.digitalAccountNumber)
  ) {
    sections
      .find((section) => section.id === "bankAccount")
      ?.missing.push("Conexão com PagBank ainda não realizada");
  }

  if (!hasFilledValue(partnerData.pixkey)) {
    sections
      .find((section) => section.id === "pixKey")
      ?.missing.push("Informe uma chave Pix válida");
  }

  if (!partnerData.businessHours || partnerData.businessHours.length === 0) {
    sections
      .find((section) => section.id === "businessHours")
      ?.missing.push("Cadastre pelo menos um horário de funcionamento");
  }

  const additionalSection = sections.find(
    (section) => section.id === "additionalInfo"
  );
  if (additionalSection) {
    if (!hasFilledValue(partnerData.description)) {
      additionalSection.missing.push("Descrição completa");
    }
    if (!hasFilledValue(partnerData.whatsapp)) {
      additionalSection.missing.push("Número de WhatsApp");
    }
    if (!hasFilledValue(partnerData.instagram)) {
      additionalSection.missing.push("Link do Instagram");
    }
    if (!hasImageType(partnerData, "LOGO")) {
      additionalSection.missing.push("Logo do negócio");
    }
    if (!hasImageType(partnerData, "COVER")) {
      additionalSection.missing.push("Imagem de banner");
    }
  }

  return sections;
};

export const hasPendingOnboardingItems = (
  sections: ChecklistSection[]
): boolean => sections.some((section) => section.missing.length > 0);

export interface OnboardingValidationResult {
  sections: ChecklistSection[];
  hasPending: boolean;
}

export const validateOnboardingData = (
  partner?: PartnerDetailsType | null
): OnboardingValidationResult => {
  const sections = buildOnboardingChecklist(partner);
  return {
    sections,
    hasPending: hasPendingOnboardingItems(sections),
  };
};

const getSkipKey = (partnerId?: string): string | null => {
  if (!partnerId) {
    return null;
  }
  return `${SKIP_KEY_PREFIX}${partnerId}`;
};

const getStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage || window.sessionStorage || null;
};

export const shouldSkipOnboarding = (partnerId?: string): boolean => {
  const key = getSkipKey(partnerId);
  const storage = getStorage();
  if (!key || !storage) {
    return false;
  }
  return storage.getItem(key) === "true";
};

export const markOnboardingSkipped = (partnerId?: string): void => {
  const key = getSkipKey(partnerId);
  const storage = getStorage();
  if (!key || !storage) {
    return;
  }
  storage.setItem(key, "true");
};

export const clearOnboardingSkip = (partnerId?: string): void => {
  const key = getSkipKey(partnerId);
  const storage = getStorage();
  if (!key || !storage) {
    return;
  }
  storage.removeItem(key);
};

